import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Camera,
  X as XIcon,
  Loader2,
  Sparkles,
  Save,
  RotateCcw,
  Crown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable AI Food Photo button.
 * Props:
 *  - variant:    "primary" | "ghost"  visual style
 *  - label:      override button label
 *  - autoLog:    if true, the server also auto-logs the result into Quick Log
 *  - quickLogType: passes through when autoLog=true ('shake' | 'restaurant' | 'cheat' | 'other' ...)
 *  - onSaved:    callback fired after a save (gets the food object back)
 */
export default function FoodPhotoButton({
  variant = "primary",
  label = "Snap Meal",
  autoLog = false,
  quickLogType = "other",
  onSaved,
}) {
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [error, setError] = useState(null);
  const [paywallHit, setPaywallHit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [servings, setServings] = useState(1);

  const reset = () => {
    setResult(null);
    setError(null);
    setImagePreview(null);
    setPaywallHit(null);
    setServings(1);
  };

  const close = () => {
    setOpen(false);
    reset();
  };

  const onPickFile = () => fileRef.current?.click();

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result || "";
        const comma = result.indexOf(",");
        resolve(comma > -1 ? result.slice(comma + 1) : result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFile = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Use a JPG, PNG, or WEBP image.");
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Image too large (max 6 MB). Try a smaller photo.");
      return;
    }
    setOpen(true);
    setAnalyzing(true);
    setError(null);
    setPaywallHit(null);
    setResult(null);
    setImagePreview(URL.createObjectURL(file));
    try {
      const image_base64 = await fileToBase64(file);
      const { data } = await axios.post(`${API}/vitals/food-photo`, {
        image_base64,
        mime: file.type,
        auto_log: false, // confirm flow — save happens after user confirms
      });
      setResult(data);
      if (data?.food?.name === "Not Food") {
        toast.info("That didn't look like food — try another photo.");
      } else {
        toast.success(`📸 ${data.food.name} · ${data.food.confidence} confidence`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && detail?.error === "premium_required") {
        setPaywallHit(detail);
      } else {
        const msg = typeof detail === "string" ? detail : "Couldn't analyze the photo.";
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (file) handleFile(file);
  };

  const save = async () => {
    if (!result?.food) return;
    if (result.food.name === "Not Food") {
      toast.error("Not a meal — try a different photo.");
      return;
    }
    setSaving(true);
    try {
      const f = result.food;
      const portion = Math.max(0.25, parseFloat(servings) || 1);
      const scaleInt = (v) => (v == null || v === "" ? null : Math.round(Number(v) * portion));
      // 🍳 Feb 2026 owner canon fix: pass the FULL macro payload so the
      // Nutrition Macros & Micros panel adds P/C/F/fiber/sugar/sodium
      // instead of showing "Includes N Quick Log extras · X kcal" with
      // all macros reading 0. Backend `QuickAddIn` accepts these fields.
      await axios.post(`${API}/vitals/quick`, {
        type: quickLogType,
        name: f.name,
        calories: Math.round((f.calories || 0) * portion),
        protein_g: scaleInt(f.protein_g),
        carbs_g:   scaleInt(f.carbs_g),
        fat_g:     scaleInt(f.fat_g),
        fiber_g:   scaleInt(f.fiber_g),
        sugar_g:   scaleInt(f.sugar_g),
        sodium_mg: scaleInt(f.sodium_mg),
        time: "",
        guilt_free: false,
      });
      toast.success(`✅ Logged ${f.name} (+2 PT)`);
      onSaved?.({ ...f, portion });
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const btnClass =
    variant === "primary"
      ? "btn-omni btn-red"
      : "btn-omni btn-outline-gold text-xs";

  return (
    <>
      <button
        data-testid="food-photo-btn"
        type="button"
        onClick={onPickFile}
        className={btnClass}
      >
        <Camera size={14} />
        {label}
      </button>
      <input
        ref={fileRef}
        data-testid="food-photo-input"
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        className="hidden"
        onChange={onChange}
      />

      {open && (
        <div
          data-testid="food-photo-modal"
          className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
        >
          <div
            className="bg-[#0F0F0F] border-2 border-[#D4AF37] max-w-md w-full p-6 max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
                  AI Food Vision · Gemini 3
                </div>
                <h3 className="font-display text-2xl text-white">Snap & Identify</h3>
              </div>
              <button
                data-testid="food-photo-close"
                onClick={close}
                className="text-neutral-500 hover:text-white"
              >
                <XIcon size={22} />
              </button>
            </div>

            {imagePreview && (
              <div className="mb-4 border border-zinc-800 overflow-hidden bg-black">
                <img
                  src={imagePreview}
                  alt="Your meal"
                  data-testid="food-photo-preview"
                  className="w-full max-h-80 object-contain"
                />
              </div>
            )}

            {analyzing && (
              <div className="text-center py-8 text-neutral-400">
                <Loader2 className="animate-spin mx-auto mb-2 text-[#D4AF37]" size={28} />
                <p className="text-sm">Identifying your meal…</p>
                <p className="text-[10px] uppercase tracking-widest text-neutral-600 mt-1">
                  Powered by Gemini 3 Flash
                </p>
              </div>
            )}

            {paywallHit && (
              <div
                data-testid="food-photo-paywall"
                className="border-2 border-[#D4AF37] bg-[#1a0d2b]/60 p-4 text-center"
              >
                <Crown size={28} className="mx-auto text-[#D4AF37] mb-2" />
                <p className="font-display text-lg text-white mb-1">Daily limit reached</p>
                <p className="text-sm text-neutral-300 mb-3">{paywallHit.message}</p>
                <a
                  href="#plans"
                  onClick={close}
                  className="btn-omni btn-red w-full"
                >
                  <Sparkles size={14} /> Upgrade to Premium
                </a>
              </div>
            )}

            {error && !paywallHit && (
              <div className="border border-[#DC2626] bg-[#DC2626]/10 p-3 text-center mb-3">
                <p className="text-sm text-red-300">{error}</p>
                <button
                  onClick={() => {
                    reset();
                    onPickFile();
                  }}
                  className="btn-omni btn-outline-gold text-xs mt-2"
                >
                  <RotateCcw size={12} /> Try Another Photo
                </button>
              </div>
            )}

            {result?.food && !analyzing && (
              <div data-testid="food-photo-result" className="space-y-3">
                <div className="bg-[#0A0A0A] border border-zinc-800 p-3">
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <h4 className="font-display text-xl text-white truncate">
                      {result.food.name}
                    </h4>
                    <span
                      className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${
                        result.food.confidence === "high"
                          ? "border-emerald-500 text-emerald-300"
                          : result.food.confidence === "medium"
                          ? "border-[#D4AF37] text-[#D4AF37]"
                          : "border-neutral-600 text-neutral-400"
                      }`}
                    >
                      {result.food.confidence}
                    </span>
                  </div>
                  <p className="text-[10px] uppercase tracking-widest text-neutral-500">
                    Per {result.food.serving}
                  </p>
                  {result.food.notes && (
                    <p className="text-xs text-neutral-400 mt-2 italic">{result.food.notes}</p>
                  )}
                </div>

                {/* Macros grid */}
                <div className="grid grid-cols-4 gap-1.5">
                  <Stat label="kcal" value={result.food.calories} />
                  <Stat label="Protein" value={`${result.food.protein_g}g`} />
                  <Stat label="Carbs" value={`${result.food.carbs_g}g`} />
                  <Stat label="Fat" value={`${result.food.fat_g}g`} />
                </div>

                {/* Portion adjustment */}
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">
                    Servings
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid="food-photo-portion-minus"
                      onClick={() => setServings((s) => Math.max(0.25, parseFloat(s) - 0.25))}
                      className="w-8 h-8 bg-[#0A0A0A] border border-zinc-700 text-white"
                    >
                      −
                    </button>
                    <input
                      data-testid="food-photo-portion"
                      type="number"
                      step="0.25"
                      min="0.25"
                      max="20"
                      value={servings}
                      onChange={(e) => setServings(e.target.value)}
                      className="input-omni flex-1 text-center"
                    />
                    <button
                      data-testid="food-photo-portion-plus"
                      onClick={() => setServings((s) => parseFloat(s) + 0.25)}
                      className="w-8 h-8 bg-[#0A0A0A] border border-zinc-700 text-white"
                    >
                      +
                    </button>
                    <span className="text-xs text-neutral-500 ml-1">×</span>
                  </div>
                  <p className="text-[10px] text-neutral-600 mt-1 tabular-nums">
                    Scaled: {Math.round((result.food.calories || 0) * (parseFloat(servings) || 1))} kcal ·
                    P {Math.round((result.food.protein_g || 0) * (parseFloat(servings) || 1))}g ·
                    C {Math.round((result.food.carbs_g || 0) * (parseFloat(servings) || 1))}g ·
                    F {Math.round((result.food.fat_g || 0) * (parseFloat(servings) || 1))}g
                  </p>
                </div>

                <div className="flex gap-2">
                  <button
                    data-testid="food-photo-retry"
                    onClick={() => {
                      reset();
                      onPickFile();
                    }}
                    className="btn-omni btn-outline-gold flex-1"
                  >
                    <RotateCcw size={12} /> Retake
                  </button>
                  <button
                    data-testid="food-photo-save"
                    onClick={save}
                    disabled={saving || result.food.name === "Not Food"}
                    className="btn-omni btn-red flex-1 disabled:opacity-50"
                  >
                    {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                    Log Meal (+2 PT)
                  </button>
                </div>

                {result.remaining_today !== null && result.remaining_today !== undefined && !result.is_premium && (
                  <p className="text-[10px] uppercase tracking-widest text-neutral-600 text-center">
                    {result.remaining_today} of {result.free_per_day} free scans left today
                  </p>
                )}
                {result.is_premium && (
                  <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] text-center">
                    Premium · Unlimited scans 🐦‍🔥
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#0A0A0A] border border-zinc-800 p-2 text-center">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="font-display text-lg text-white tabular-nums">{value}</div>
    </div>
  );
}
